import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { ElectricalQuestionPhotos } from "./ElectricalPhotos";
import { PreviousLine } from "./ElectricalCarry";

/**
 * A native <option> cannot wrap - every browser renders its dropdown list as
 * single-line text no matter what CSS says, which is exactly the problem for
 * an option that is the checklist's own full sentence. react-select's menu
 * is ordinary divs, so long text wraps like any other block element; these
 * styles just widen it to match .q-comment and turn wrapping on explicitly,
 * since react-select's own default leans toward truncating a long single
 * value.
 */
const pickerStyles = {
  // `state.selectProps.hasError` rather than a CSS class: react-select's own
  // emotion-generated classes are injected after the stylesheet and often
  // win a specificity tie against a plain external selector, which is what
  // silently stopped .q-cmt.need from ever reddening this control the way it
  // still does the plain textarea below.
  control: (base, state) => ({
    ...base,
    minWidth: 320,
    minHeight: 44,
    fontSize: 13,
    borderColor: state.selectProps.hasError ? "var(--no)" : "var(--rule)",
    backgroundColor: state.selectProps.hasError ? "#fdf6f5" : base.backgroundColor,
  }),
  valueContainer: (base) => ({ ...base, whiteSpace: "normal" }),
  singleValue: (base) => ({ ...base, whiteSpace: "normal", wordBreak: "break-word" }),
  placeholder: (base) => ({ ...base, fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, minWidth: 320 }),
  option: (base) => ({
    ...base,
    whiteSpace: "normal",
    wordBreak: "break-word",
    fontSize: 13,
  }),
};

/**
 * One checkpoint on the capture screen. Modeled on Csa/CsaQuestionCard.js,
 * with structural differences from CSA's card:
 *
 *   - No Tier 3 "tidy this up" assist. CSA's version calls api.assist, backed
 *     by assistRules.js and a capture_assist prompt; neither has an
 *     Electrical equivalent yet (sql/14_elec_capture_assist.sql seeds the
 *     prompt row and the elec_answer.comment_assisted flag, but no net.js
 *     function calls it). Left out rather than half-wired.
 *
 *   - Criticality is a read-only badge, not a button the auditor presses.
 *     It is the checkpoint's own inherent severity (e.g. "DANGER signage
 *     missing" is always High) - fixed by the framework, not decided per
 *     visit. The one real choice per checkpoint is Status, which is why this
 *     card has one button group where CSA's has one too (yes/partial/no/na),
 *     just with Electrical's own status vocabulary.
 *
 *   - Both the observation AND the recommendation are chosen, not typed, in
 *     the common case: each is its own dropdown offering the checklist's own
 *     default text plus "Other"; only "Other" opens a text area for a
 *     one-off entry specific to this checkpoint. Most findings on a given
 *     checkpoint read exactly like the checklist's own wording, so typing it
 *     out again on every audit is the repeated work this removes.
 *
 * Marking and writing stay separated (design pattern carried from CSA):
 * tapping a status saves at once; the two pickers are present but only a
 * gate when the rule below says so.
 */

/*
 * Placeholder until a sector-options endpoint exists (elec_option is seeded
 * per sector_type in the admin settings DB, but nothing in net.js reads it
 * yet). YES is the one value every bulk-compliant and legacy default already
 * assumes, so it anchors the list regardless of what a sector adds.
 */
const STATUS_OPTIONS = ["YES", "NO", "NA", "PARTIAL"];

/**
 * Both the observation and the recommendation are mandatory whenever a
 * checkpoint is marked anything other than Yes - No, N/A and Partial are all
 * a client reads as "this did not simply pass", and that always needs a
 * reason and a fix on record. Yes needs neither: the checklist's own wording
 * already covers a pass.
 */
function isMandatory(status) {
  return !!status && String(status).toUpperCase() !== "YES";
}

/**
 * Recommendation is the one exception - marked N/A, there is nothing to
 * recommend fixing on something that does not apply. Observation still has
 * to explain why it is N/A, which is why only this one relaxes.
 */
function needsRecommendation(status) {
  return isMandatory(status) && String(status).toUpperCase() !== "NA";
}

const CRITICALITY_CLASS = {
  extreme: "a-no",
  high: "a-no",
  medium: "a-partial",
  low: "a-na",
};

/**
 * A real checkpoint's criticality is fixed by the framework (see the badge
 * below, which is read-only for exactly that reason). An off-checklist
 * observation has no framework row behind it to fix one, so
 * ElectricalExtras.js's own criticality is chosen by the auditor instead -
 * these are the buttons that choice uses, sharing CRITICALITY_CLASS so the
 * colour means the same thing in both places.
 */
const CRITICALITY_OPTIONS = ["LOW", "MEDIUM", "HIGH", "EXTREME"];

export default function ElectricalQuestionCard({
  checkpoint,
  auditId,
  answer,
  photos,
  disabled,
  scopedOutBy,
  previous,
  onAnswer,
  onObservation,
  onRecommendation,
  onConfirm,
  onPhotosChanged,
}) {
  const [showRef, setShowRef] = useState(false);

  const status = (answer && answer.status) || null;
  const criticality = (answer && answer.criticality) || checkpoint.criticality;
  const required = isMandatory(status);
  const recommendationRequired = needsRecommendation(status);

  // A carried row is a proposal until the auditor has looked at it.
  const carried = answer && answer.carried_from_audit_id ? answer : null;
  const confirmed = !!(answer && answer.is_confirmed);

  const cls = ["q",
    status ? "a-" + String(status).toLowerCase().replace(/[^a-z]/g, "") : "",
    carried && !confirmed ? "carried-open" : "",
  ].filter(Boolean).join(" ");

  const critClass = CRITICALITY_CLASS[String(criticality || "").toLowerCase()] || "a-na";

  return (
    <div className={cls} id={"q-" + checkpoint.code}>
      <div className="q-code">{checkpoint.code}</div>
      <div className="q-text">{checkpoint.check_points}</div>

      {criticality ? (
        <span className={"q-evidence " + critClass} title="Set by the checklist, not the auditor">
          {criticality}
        </span>
      ) : null}

      <div className="q-answers">
        {STATUS_OPTIONS.map((o) => (
          <button
            key={o}
            type="button"
            className={"ans" + (String(status || "").toLowerCase() === o.toLowerCase() ? " on-" + o.toLowerCase().replace(/[^a-z]/g, "") : "")}
            disabled={disabled}
            aria-pressed={String(status || "").toLowerCase() === o.toLowerCase()}
            onClick={() => {
              // Tapping the current status clears it, so a mis-tap is undoable
              // without a separate control.
              onAnswer(checkpoint.code, status === o ? null : o);
            }}
          >
            {o}
          </button>
        ))}
      </div>

      {scopedOutBy ? (
        <div className="q-hint">Scoped out — {scopedOutBy}</div>
      ) : null}

      <PreviousLine
        previous={previous}
        carried={carried}
        confirmed={confirmed}
        disabled={disabled}
        onConfirm={() => onConfirm && onConfirm(checkpoint.code)}
      />

      <FieldPicker
        idPrefix="obs"
        label="Observation"
        checkpointCode={checkpoint.code}
        templateText={checkpoint.observation}
        savedValue={answer && answer.observation}
        required={required}
        disabled={disabled}
        onSave={onObservation}
      />

      <FieldPicker
        idPrefix="rec"
        label="Recommendation"
        checkpointCode={checkpoint.code}
        templateText={checkpoint.recommendations}
        savedValue={answer && answer.recommendation}
        required={recommendationRequired}
        disabled={disabled}
        onSave={onRecommendation}
      />

      <ElectricalQuestionPhotos
        auditId={auditId}
        checkpointCode={checkpoint.code}
        photos={photos}
        disabled={disabled}
        onChange={onPhotosChanged}
      />

      <button type="button" className="ref-toggle" onClick={() => setShowRef(!showRef)}>
        {showRef ? "▾ Hide reference" : "▸ Reference"}
      </button>

      {showRef ? (
        <dl className="ref">
          {checkpoint.observation ? (
            <>
              <dt>Default finding if non-compliant</dt>
              <dd>{checkpoint.observation}</dd>
            </>
          ) : null}
          {checkpoint.recommendations ? (
            <>
              <dt>Recommendation</dt>
              <dd>{checkpoint.recommendations}</dd>
            </>
          ) : null}
          {checkpoint.legal_reference ? (
            <>
              <dt>Reference</dt>
              <dd>{checkpoint.legal_reference}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * One choose-or-type field, shared shape for both Observation and
 * Recommendation: a dropdown offering the checklist's own template text
 * plus "Other", which alone opens a text area for a one-off entry.
 */
function FieldPicker({ idPrefix, label, checkpointCode, templateText, savedValue, required, disabled, onSave }) {
  // "default"  the checklist's own template text, picked as-is
  // "other"    a one-off entry, typed below
  // ""         nothing chosen yet
  const [choice, setChoice] = useState(() => initialChoice(savedValue, templateText));
  const [draft, setDraft] = useState(savedValue || "");
  const timer = useRef(null);
  const dirty = useRef(false);

  /*
   * Adopt the server's text only while the auditor is not mid-sentence.
   * Without the dirty check, a roll-up refresh landing between keystrokes
   * would snap the box back to the last saved value and eat the typing.
   */
  useEffect(() => {
    if (dirty.current) return;
    setDraft(savedValue || "");
    setChoice(initialChoice(savedValue, templateText));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedValue]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flush = (text) => {
    clearTimeout(timer.current);
    if (!dirty.current) return;
    dirty.current = false;
    onSave(checkpointCode, text);
  };

  /** Picking the checklist's own text or clearing the choice saves at once - it is a discrete decision, not typing. */
  const handleChoice = (opt) => {
    const val = opt ? opt.value : "";
    clearTimeout(timer.current);
    setChoice(val);

    if (val === "default") {
      setDraft(templateText);
      dirty.current = true;
      flush(templateText);
    } else if (val === "other") {
      setDraft("");
    } else {
      setDraft("");
      dirty.current = true;
      flush("");
    }
  };

  const choiceOptions = [
    { value: "default", label: templateText },
    { value: "other", label: "Other" },
  ];
  const selectedOption = choiceOptions.find((o) => o.value === choice) || null;

  const handleType = (e) => {
    const text = e.target.value;
    setDraft(text);
    dirty.current = true;
    clearTimeout(timer.current);
    // Long enough that a sentence saves as one write, short enough that
    // walking away from the tablet does not lose it.
    timer.current = setTimeout(() => flush(text), 900);
  };

  const filled = choice === "default" || (choice === "other" && !!draft.trim());
  const needsNote = required && !filled;

  return (
    <div className={"q-cmt" + (needsNote ? " need" : "")}>
      <label htmlFor={idPrefix + "-" + checkpointCode}>
        {label}
        {required ? <span className="req"> — required</span> : null}
      </label>
      <Select
        inputId={idPrefix + "-" + checkpointCode}
        className="q-comment-select"
        classNamePrefix="q-comment-select"
        placeholder="— Choose —"
        options={choiceOptions}
        value={selectedOption}
        isDisabled={disabled}
        hasError={needsNote}
        onChange={handleChoice}
        styles={pickerStyles}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      />

      {choice === "other" ? (
        <textarea
          id={idPrefix + "-other-" + checkpointCode}
          className="q-comment"
          rows={2}
          value={draft}
          disabled={disabled}
          placeholder="What you actually saw, where, and how many."
          onChange={handleType}
          onBlur={() => flush(draft)}
          style={{ marginTop: 6 }}
        />
      ) : null}
    </div>
  );
}

/** "default" if the saved value is exactly the checklist's own template text, "other" if it's custom, else unset. */
function initialChoice(value, templateText) {
  if (!value) return "";
  return value === templateText ? "default" : "other";
}

export { isMandatory, needsRecommendation, STATUS_OPTIONS, CRITICALITY_OPTIONS, CRITICALITY_CLASS };
